import DOMPurify from "dompurify";

/**
 * 1NOW-1643 — sanitiser for website-builder content.
 *
 * Section headings, descriptions, footer copy and FAQ answers are authored in
 * the FMS rich-text editor and rendered here with `dangerouslySetInnerHTML`.
 * Nothing sanitised them, so anything an operator could store — or paste in as
 * markup — executed in the renter's browser, on the same origin as checkout.
 *
 * The allowlist below covers what the editor actually emits (basic marks,
 * lists, headings, links, line breaks) plus the inline `style`/`class`
 * attributes it writes for colour and alignment. Anything else — `<script>`,
 * `<iframe>`, `on*` handlers, `javascript:` URLs — is dropped.
 *
 * If a tenant reports lost formatting after this ships, widen ALLOWED_TAGS
 * rather than reaching for the raw string.
 */
const ALLOWED_TAGS = [
  "a", "b", "blockquote", "br", "code", "div", "em", "h1", "h2", "h3", "h4",
  "h5", "h6", "hr", "i", "li", "ol", "p", "pre", "s", "small", "span",
  "strike", "strong", "sub", "sup", "u", "ul",
];

const ALLOWED_ATTR = ["class", "style", "href", "target", "rel", "title"];

/**
 * DOMPurify does not parse CSS — with `style` on the allowlist it passes the
 * declaration string through verbatim. `style` has to stay allowed, because the
 * builder's line-height format writes `line-height` inline.
 *
 * Script-via-CSS is dead in modern browsers, so this is not an XSS hole. Two
 * things do still work and are worth removing:
 *
 *   url(...)   loads an external resource from a tenant's booking page, which
 *              leaks the renter's IP and user-agent to whoever authored it.
 *   position   fixed/absolute/sticky allows a full-viewport overlay over the
 *              booking flow — UI redressing.
 *
 * expression() and @import are inert but cost nothing to drop.
 *
 * Declaration-level, not attribute-level: one bad property is removed and the
 * legitimate ones beside it survive. `color`, `background-color` and
 * `line-height` are unaffected.
 */
const UNSAFE_DECLARATION = /url\s*\(|expression\s*\(|@import|javascript:|-moz-binding/i;
const UNSAFE_POSITION = /^(fixed|absolute|sticky)$/i;

const scrubStyle = (style: string): string =>
  style
    .split(";")
    .filter((declaration) => {
      const separator = declaration.indexOf(":");
      if (separator === -1) return false;
      if (UNSAFE_DECLARATION.test(declaration)) return false;
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();
      if (property === "position" && UNSAFE_POSITION.test(value)) return false;
      return true;
    })
    .map((declaration) => declaration.trim())
    .join("; ");

/**
 * Returns HTML safe to hand to `dangerouslySetInnerHTML`.
 * Nullish input yields an empty string, so callers don't need their own guard.
 */
export const sanitizeHtml = (html?: string | null): string => {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Keep the text of anything stripped, so removing a stray tag never
    // silently deletes a tenant's copy.
    KEEP_CONTENT: true,
  });
};

// Registered once at module load. Duck-typed rather than `instanceof
// HTMLElement` so the hook also works under jsdom, where the node comes from a
// different realm and the instanceof check would silently never fire.
if (typeof window !== "undefined") {
  DOMPurify.addHook("afterSanitizeAttributes", (node: any) => {
    if (typeof node?.getAttribute !== "function") return;

    // Links in tenant copy point off-site; don't let them reach the opener.
    if (node.tagName === "A" && node.hasAttribute("href")) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }

    if (node.hasAttribute("style")) {
      const scrubbed = scrubStyle(node.getAttribute("style") || "");
      if (scrubbed) node.setAttribute("style", scrubbed);
      else node.removeAttribute("style");
    }
  });
}

export default sanitizeHtml;
